import React, { useState, useRef, useCallback, useEffect } from 'react';
import { IoIosMusicalNotes } from 'react-icons/io';
import {
  Upload, Play, Pause, SkipBack, Plus, Trash2, Wand2,
  ImagePlus, RefreshCw, ChevronLeft, Download, Film,
  Type, Layers, Clock, Sparkles,
} from 'lucide-react';
import { generateSceneImage } from '../endpoints/music-video/generate-scene-image_POST.schema';
import { generateVideo } from '../endpoints/video/generate_POST.schema';
import { uploadAudio } from '../endpoints/music-video/upload-audio_POST.schema';
import css from './MusicVideoEditor.module.css';

// ── Types ──

export type VideoStyle = 'anime' | 'cartoon' | 'cinematic' | 'promo' | 'commercial';

interface TextOverlay {
  id: string;
  time: number;
  duration: number;
  text: string;
  style: 'title' | 'subtitle' | 'caption' | 'cta';
}

interface VideoScene {
  id: string;
  imageUrl: string | null;
  videoUrl: string | null;
  prompt: string;
  startTime: number;
  duration: number;
  kenBurns: 'zoomIn' | 'zoomOut' | 'panRight' | 'panLeft';
  generating: boolean;
}

interface VideoProject {
  title: string;
  style: VideoStyle;
  audioUrl: string | null;
  audioDuration: number;
  totalDuration: number;
  scenes: VideoScene[];
  textOverlays: TextOverlay[];
  description: string;
}

const STYLE_CONFIG: Record<VideoStyle, {
  label: string;
  icon: React.ReactNode;
  defaultPromptPrefix: string;
  color: string;
  gradient: string;
  sceneDuration: number;
  defaultSceneCount: number;
}> = {
  anime: {
    label: 'Anime',
    icon: <Sparkles size={16} />,
    defaultPromptPrefix: 'Anime style, Japanese animation aesthetic, vibrant colors, expressive characters, dynamic lighting,',
    color: '#ff6b9d',
    gradient: 'linear-gradient(135deg, #ff6b9d, #c44dff)',
    sceneDuration: 5,
    defaultSceneCount: 6,
  },
  cartoon: {
    label: 'Cartoon',
    icon: <Sparkles size={16} />,
    defaultPromptPrefix: 'Cartoon style, colorful animation, exaggerated expressions, playful, fun,',
    color: '#00d2ff',
    gradient: 'linear-gradient(135deg, #00d2ff, #3a7bd5)',
    sceneDuration: 4,
    defaultSceneCount: 8,
  },
  cinematic: {
    label: 'Cinematic',
    icon: <Film size={16} />,
    defaultPromptPrefix: 'Cinematic, dramatic lighting, film grain, widescreen 2.39:1, depth of field, professional color grading,',
    color: '#ffd700',
    gradient: 'linear-gradient(135deg, #ffd700, #ff8c00)',
    sceneDuration: 6,
    defaultSceneCount: 5,
  },
  promo: {
    label: 'Promo',
    icon: <Sparkles size={16} />,
    defaultPromptPrefix: 'Professional promotional video, clean modern design, corporate, polished,',
    color: '#00e676',
    gradient: 'linear-gradient(135deg, #00e676, #1de9b6)',
    sceneDuration: 4,
    defaultSceneCount: 6,
  },
  commercial: {
    label: 'Commercial',
    icon: <Sparkles size={16} />,
    defaultPromptPrefix: 'High-end commercial advertisement, premium product photography, studio lighting, aspirational,',
    color: '#ff9100',
    gradient: 'linear-gradient(135deg, #ff9100, #ff6d00)',
    sceneDuration: 3,
    defaultSceneCount: 8,
  },
};

const KEN_BURNS_OPTIONS = [
  { value: 'zoomIn', label: 'Zoom In' },
  { value: 'zoomOut', label: 'Zoom Out' },
  { value: 'panRight', label: 'Pan Right' },
  { value: 'panLeft', label: 'Pan Left' },
] as const;

const KEN_BURNS_CLASS: Record<string, string> = {
  zoomIn: css.kenBurnsZoomIn,
  zoomOut: css.kenBurnsZoomOut,
  panRight: css.kenBurnsPanRight,
  panLeft: css.kenBurnsPanLeft,
};

const SCENE_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#f39c12',
  '#9b59b6', '#1abc9c', '#e67e22', '#2980b9',
];

const TEXT_STYLE_MAP: Record<string, React.CSSProperties> = {
  title: { fontSize: 'clamp(1.5rem, 4vw, 3rem)', fontWeight: 800, textTransform: 'uppercase' as const },
  subtitle: { fontSize: 'clamp(1rem, 2.5vw, 1.8rem)', fontWeight: 600 },
  caption: { fontSize: 'clamp(0.8rem, 2vw, 1.2rem)', fontWeight: 400, fontStyle: 'italic' as const },
  cta: { fontSize: 'clamp(1rem, 2.5vw, 1.6rem)', fontWeight: 700, background: 'rgba(255,215,0,0.9)', color: '#000', padding: '8px 24px', borderRadius: '8px', display: 'inline-block' },
};

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ── Component ──

export function VideoEditor({ style }: { style: VideoStyle }) {
  const config = STYLE_CONFIG[style];

  const [project, setProject] = useState<VideoProject>(() => {
    const totalDur = config.sceneDuration * config.defaultSceneCount;
    return {
      title: `Untitled ${config.label} Video`,
      style,
      audioUrl: null,
      audioDuration: 0,
      totalDuration: totalDur,
      scenes: Array.from({ length: config.defaultSceneCount }, (_, i) => ({
        id: uid(),
        imageUrl: null,
        videoUrl: null,
        prompt: '',
        startTime: i * config.sceneDuration,
        duration: config.sceneDuration,
        kenBurns: KEN_BURNS_OPTIONS[i % 4].value,
        generating: false,
      })),
      textOverlays: [],
      description: '',
    };
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeSceneIdx, setActiveSceneIdx] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');
  const [generatingVideo, setGeneratingVideo] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Playback ──
  const tick = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      setCurrentTime(audioRef.current.currentTime);
      animFrameRef.current = requestAnimationFrame(tick);
    }
  }, []);

  // Timer-based playback when no audio
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const start = Date.now() - currentTime * 1000;
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      if (elapsed >= project.totalDuration) {
        setCurrentTime(0);
        setIsPlaying(false);
        if (timerRef.current) clearInterval(timerRef.current);
        return;
      }
      setCurrentTime(elapsed);
    }, 33);
  }, [currentTime, project.totalDuration]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (project.audioUrl && audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPlaying(true);
        animFrameRef.current = requestAnimationFrame(tick);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
        cancelAnimationFrame(animFrameRef.current);
      }
    } else {
      // Timer-based playback
      if (isPlaying) {
        stopTimer();
        setIsPlaying(false);
      } else {
        setIsPlaying(true);
        startTimer();
      }
    }
  }, [project.audioUrl, isPlaying, tick, startTimer, stopTimer]);

  const restart = useCallback(() => {
    stopTimer();
    if (audioRef.current) audioRef.current.currentTime = 0;
    setCurrentTime(0);
    setIsPlaying(false);
  }, [stopTimer]);

  useEffect(() => {
    return () => {
      stopTimer();
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [stopTimer]);

  // Active scene based on time
  useEffect(() => {
    const { scenes } = project;
    for (let i = scenes.length - 1; i >= 0; i--) {
      if (currentTime >= scenes[i].startTime) {
        setActiveSceneIdx(i);
        return;
      }
    }
    setActiveSceneIdx(0);
  }, [currentTime, project.scenes]);

  // Active text overlay
  const activeOverlay = (() => {
    for (let i = project.textOverlays.length - 1; i >= 0; i--) {
      const o = project.textOverlays[i];
      if (currentTime >= o.time && currentTime <= o.time + o.duration) return o;
    }
    return null;
  })();

  // ── Audio ──
  const onAudioLoaded = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const dur = audio.duration || 30;
    setProject((p) => ({ ...p, audioDuration: dur, totalDuration: Math.max(p.totalDuration, dur) }));
  }, []);

  const handleUploadAudio = useCallback(() => { fileInputRef.current?.click(); }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatusMsg('Uploading audio...');
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await uploadAudio({ data: dataUrl, fileName: file.name });
      setProject((p) => ({ ...p, audioUrl: result.audioUrl }));
      setStatusMsg('Audio uploaded!');
    } catch (err: any) {
      setStatusMsg(`Upload error: ${err.message}`);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ── Generate scene image ──
  const handleGenerateSceneImage = useCallback(async (sceneIdx: number) => {
    const scene = project.scenes[sceneIdx];
    if (!scene || !scene.prompt.trim()) return;

    setProject((p) => {
      const scenes = [...p.scenes];
      scenes[sceneIdx] = { ...scenes[sceneIdx], generating: true };
      return { ...p, scenes };
    });
    setStatusMsg(`Generating image for Scene ${sceneIdx + 1}...`);

    try {
      const result = await generateSceneImage({
        prompt: `${config.defaultPromptPrefix} ${scene.prompt}`,
        style: config.label,
        width: 1280,
        height: 720,
      });
      setProject((p) => {
        const scenes = [...p.scenes];
        scenes[sceneIdx] = { ...scenes[sceneIdx], imageUrl: result.imageUrl, generating: false };
        return { ...p, scenes };
      });
      setStatusMsg(`Scene ${sceneIdx + 1} image ready!`);
    } catch (err: any) {
      setProject((p) => {
        const scenes = [...p.scenes];
        scenes[sceneIdx] = { ...scenes[sceneIdx], generating: false };
        return { ...p, scenes };
      });
      setStatusMsg(`Error: ${err.message}`);
    }
  }, [project.scenes, config]);

  // ── Generate video clip for scene ──
  const handleGenerateVideoClip = useCallback(async (sceneIdx: number) => {
    const scene = project.scenes[sceneIdx];
    if (!scene || !scene.prompt.trim()) return;

    setProject((p) => {
      const scenes = [...p.scenes];
      scenes[sceneIdx] = { ...scenes[sceneIdx], generating: true };
      return { ...p, scenes };
    });
    setStatusMsg(`Generating video for Scene ${sceneIdx + 1}... (this may take a few minutes)`);

    try {
      const result = await generateVideo({
        prompt: `${config.defaultPromptPrefix} ${scene.prompt}`,
        style,
        model: style === 'cinematic' ? 'veo3' : style === 'anime' ? 'kling' : 'veo-fast',
        imageUrl: scene.imageUrl || undefined,
      });
      setProject((p) => {
        const scenes = [...p.scenes];
        scenes[sceneIdx] = { ...scenes[sceneIdx], videoUrl: result.videoUrl, generating: false };
        return { ...p, scenes };
      });
      setStatusMsg(`Scene ${sceneIdx + 1} video ready!`);
    } catch (err: any) {
      setProject((p) => {
        const scenes = [...p.scenes];
        scenes[sceneIdx] = { ...scenes[sceneIdx], generating: false };
        return { ...p, scenes };
      });
      setStatusMsg(`Error: ${err.message}`);
    }
  }, [project.scenes, config, style]);

  // ── Generate ALL scene images ──
  const handleGenerateAllImages = useCallback(async () => {
    setGeneratingVideo(true);
    setStatusMsg('Generating all scene images...');
    for (let i = 0; i < project.scenes.length; i++) {
      if (project.scenes[i].prompt.trim() && !project.scenes[i].imageUrl) {
        await handleGenerateSceneImage(i);
      }
    }
    setGeneratingVideo(false);
    setStatusMsg('All images generated!');
  }, [project.scenes, handleGenerateSceneImage]);

  // ── Scene CRUD ──
  const addScene = useCallback(() => {
    setProject((p) => {
      const last = p.scenes[p.scenes.length - 1];
      const start = last ? last.startTime + last.duration : 0;
      return {
        ...p,
        totalDuration: Math.max(p.totalDuration, start + config.sceneDuration),
        scenes: [
          ...p.scenes,
          {
            id: uid(),
            imageUrl: null,
            videoUrl: null,
            prompt: '',
            startTime: start,
            duration: config.sceneDuration,
            kenBurns: KEN_BURNS_OPTIONS[p.scenes.length % 4].value,
            generating: false,
          },
        ],
      };
    });
  }, [config.sceneDuration]);

  const removeScene = useCallback((idx: number) => {
    setProject((p) => ({ ...p, scenes: p.scenes.filter((_, i) => i !== idx) }));
  }, []);

  const updateScene = useCallback((idx: number, patch: Partial<VideoScene>) => {
    setProject((p) => {
      const scenes = [...p.scenes];
      scenes[idx] = { ...scenes[idx], ...patch };
      return { ...p, scenes };
    });
  }, []);

  // ── Text overlays ──
  const addOverlay = useCallback((overlayStyle: TextOverlay['style'] = 'title') => {
    setProject((p) => ({
      ...p,
      textOverlays: [
        ...p.textOverlays,
        { id: uid(), time: currentTime, duration: 3, text: '', style: overlayStyle },
      ],
    }));
  }, [currentTime]);

  const updateOverlay = useCallback((id: string, patch: Partial<TextOverlay>) => {
    setProject((p) => ({
      ...p,
      textOverlays: p.textOverlays.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    }));
  }, []);

  const removeOverlay = useCallback((id: string) => {
    setProject((p) => ({
      ...p,
      textOverlays: p.textOverlays.filter((o) => o.id !== id),
    }));
  }, []);

  const activeScene = project.scenes[activeSceneIdx] || null;
  const PX_PER_SEC = 8;
  const timelineWidth = project.totalDuration * PX_PER_SEC;

  return (
    <div className={css.page}>
      {project.audioUrl && (
        <audio
          ref={audioRef}
          src={project.audioUrl}
          onLoadedMetadata={onAudioLoaded}
          onEnded={() => { setIsPlaying(false); cancelAnimationFrame(animFrameRef.current); }}
        />
      )}
      <input ref={fileInputRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={handleFileChange} />

      {/* ── Header ── */}
      <header className={css.header}>
        <div className={css.headerLeft}>
          <a href="/dashboard" style={{ color: 'inherit', textDecoration: 'none' }}>
            <ChevronLeft size={20} />
          </a>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: config.gradient, padding: '2px 10px', borderRadius: 12,
            fontSize: '0.75rem', fontWeight: 700, color: '#fff',
          }}>
            {config.icon} {config.label}
          </span>
          <h1>
            <input
              value={project.title}
              onChange={(e) => setProject((p) => ({ ...p, title: e.target.value }))}
              style={{ background: 'transparent', border: 'none', color: 'inherit', font: 'inherit', width: '300px' }}
            />
          </h1>
        </div>
        <div className={css.headerRight}>
          <button className={css.generateBtn} onClick={handleGenerateAllImages} disabled={generatingVideo}>
            {generatingVideo ? <RefreshCw size={14} className={css.spinner} /> : <Wand2 size={14} />}
            Generate All Images
          </button>
          <button className={css.uploadBtn} disabled>
            <Download size={14} />
            Export
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className={css.body}>
        {/* ── Sidebar ── */}
        <aside className={css.sidebar}>
          {/* Description */}
          <div className={css.sidebarSection}>
            <h3>
              <Film size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Video Description
            </h3>
            <div className={css.field}>
              <label>Describe your {config.label.toLowerCase()} video</label>
              <textarea
                placeholder={`Describe the concept, story, mood, and key scenes for your ${config.label.toLowerCase()} video...`}
                value={project.description}
                onChange={(e) => setProject((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
          </div>

          {/* Audio Section */}
          <div className={css.sidebarSection}>
            <h3>
              <IoIosMusicalNotes style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Audio Track
            </h3>
            {project.audioUrl ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <IoIosMusicalNotes size={20} color={config.color} />
                  <span style={{ fontSize: '0.85rem' }}>Track loaded ({fmtTime(project.audioDuration)})</span>
                </div>
                <button className={css.uploadBtn} onClick={handleUploadAudio}>
                  <Upload size={14} />
                  Replace Audio
                </button>
              </div>
            ) : (
              <div className={css.audioControls}>
                <button className={css.uploadBtn} onClick={handleUploadAudio}>
                  <Upload size={14} />
                  Upload Audio
                </button>
              </div>
            )}
          </div>

          {/* Scenes Grid */}
          <div className={css.sidebarSection}>
            <h3>
              <Layers size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Scenes ({project.scenes.length})
            </h3>
            <div className={css.scenesGrid}>
              {project.scenes.map((scene, i) => (
                <div
                  key={scene.id}
                  className={`${css.sceneCard} ${i === activeSceneIdx ? css.sceneCardActive : ''}`}
                  onClick={() => setActiveSceneIdx(i)}
                  style={i === activeSceneIdx ? { borderColor: config.color, boxShadow: `0 0 0 2px ${config.color}33` } : {}}
                >
                  {scene.videoUrl ? (
                    <video className={css.sceneCardImg} src={scene.videoUrl} muted style={{ objectFit: 'cover' }} />
                  ) : scene.imageUrl ? (
                    <img className={css.sceneCardImg} src={scene.imageUrl} alt={scene.prompt} />
                  ) : (
                    <div className={css.sceneCardPlaceholder}>
                      {scene.generating ? (
                        <RefreshCw size={16} className={css.spinner} />
                      ) : (
                        <ImagePlus size={16} />
                      )}
                    </div>
                  )}
                  <div className={css.sceneCardOverlay}>S{i + 1}</div>
                </div>
              ))}
            </div>
            <button
              className={css.uploadBtn}
              onClick={addScene}
              style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}
            >
              <Plus size={14} />
              Add Scene
            </button>
          </div>

          {/* Active Scene Editor */}
          {activeScene && (
            <div className={css.sidebarSection}>
              <h3>Scene {activeSceneIdx + 1} Details</h3>
              <div className={css.field}>
                <label>Scene Prompt</label>
                <textarea
                  value={activeScene.prompt}
                  onChange={(e) => updateScene(activeSceneIdx, { prompt: e.target.value })}
                  placeholder={`Describe this scene for ${config.label.toLowerCase()} style...`}
                  style={{ minHeight: 60 }}
                />
              </div>
              <div className={css.field}>
                <label>Ken Burns Effect</label>
                <select
                  value={activeScene.kenBurns}
                  onChange={(e) => updateScene(activeSceneIdx, { kenBurns: e.target.value as VideoScene['kenBurns'] })}
                >
                  {KEN_BURNS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className={css.field}>
                <label>Duration (sec)</label>
                <input
                  type="number"
                  min={1}
                  value={Math.round(activeScene.duration)}
                  onChange={(e) => updateScene(activeSceneIdx, { duration: Math.max(1, Number(e.target.value)) })}
                />
              </div>
              <div className={css.audioControls}>
                <button
                  className={css.generateBtn}
                  disabled={activeScene.generating || !activeScene.prompt.trim()}
                  onClick={() => handleGenerateSceneImage(activeSceneIdx)}
                  style={{ background: config.gradient }}
                >
                  {activeScene.generating ? <RefreshCw size={14} className={css.spinner} /> : <ImagePlus size={14} />}
                  {activeScene.imageUrl ? 'Regen Image' : 'Gen Image'}
                </button>
                <button
                  className={css.generateBtn}
                  disabled={activeScene.generating || !activeScene.prompt.trim()}
                  onClick={() => handleGenerateVideoClip(activeSceneIdx)}
                  style={{ background: config.gradient }}
                >
                  {activeScene.generating ? <RefreshCw size={14} className={css.spinner} /> : <Film size={14} />}
                  Gen Video
                </button>
                <button
                  className={css.uploadBtn}
                  style={{ background: 'var(--destructive, #e74c3c)', color: '#fff' }}
                  onClick={() => removeScene(activeSceneIdx)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Text Overlays */}
          <div className={css.sidebarSection}>
            <h3>
              <Type size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Text Overlays
            </h3>
            <div className={css.lyricsList}>
              {project.textOverlays.map((overlay) => (
                <div key={overlay.id} className={css.lyricRow}>
                  <span className={css.lyricTime}>{fmtTime(overlay.time)}</span>
                  <select
                    value={overlay.style}
                    onChange={(e) => updateOverlay(overlay.id, { style: e.target.value as TextOverlay['style'] })}
                    style={{ width: 60, fontSize: '0.7rem', padding: '2px 4px', background: 'var(--background)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--foreground)' }}
                  >
                    <option value="title">Title</option>
                    <option value="subtitle">Subtitle</option>
                    <option value="caption">Caption</option>
                    <option value="cta">CTA</option>
                  </select>
                  <input
                    className={css.lyricInput}
                    value={overlay.text}
                    onChange={(e) => updateOverlay(overlay.id, { text: e.target.value })}
                    placeholder="Text..."
                  />
                  <button
                    className={css.lyricDelete}
                    onClick={() => removeOverlay(overlay.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--muted-foreground)', cursor: 'pointer' }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className={css.audioControls} style={{ marginTop: 8 }}>
              <button className={css.uploadBtn} onClick={() => addOverlay('title')} style={{ fontSize: '0.75rem' }}>
                <Plus size={12} /> Title
              </button>
              <button className={css.uploadBtn} onClick={() => addOverlay('subtitle')} style={{ fontSize: '0.75rem' }}>
                <Plus size={12} /> Subtitle
              </button>
              <button className={css.uploadBtn} onClick={() => addOverlay('cta')} style={{ fontSize: '0.75rem' }}>
                <Plus size={12} /> CTA
              </button>
            </div>
          </div>
        </aside>

        {/* ── Center: Preview ── */}
        <div className={css.center}>
          <div className={css.previewArea}>
            {activeScene ? (
              <div className={css.previewCanvas}>
                {activeScene.videoUrl ? (
                  <video
                    src={activeScene.videoUrl}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    muted
                    autoPlay={isPlaying}
                    loop
                  />
                ) : activeScene.imageUrl ? (
                  <img
                    key={`${activeScene.id}-${activeScene.kenBurns}`}
                    className={`${css.kenBurnsImage} ${isPlaying ? KEN_BURNS_CLASS[activeScene.kenBurns] || '' : ''}`}
                    src={activeScene.imageUrl}
                    alt={activeScene.prompt}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: SCENE_COLORS[activeSceneIdx % SCENE_COLORS.length] + '33',
                    color: '#fff', fontSize: '1.2rem',
                  }}>
                    Scene {activeSceneIdx + 1} — No media
                  </div>
                )}

                {/* Text overlay */}
                {activeOverlay && activeOverlay.text && (
                  <div className={css.lyricsOverlay}>
                    <span className={css.lyricText} style={TEXT_STYLE_MAP[activeOverlay.style]}>
                      {activeOverlay.text}
                    </span>
                  </div>
                )}

                {/* Play controls */}
                <div className={css.playControls}>
                  <button className={css.playBtn} onClick={restart} style={{ background: config.color }}>
                    <SkipBack size={16} />
                  </button>
                  <button className={css.playBtn} onClick={togglePlay} style={{ background: config.color }}>
                    {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <span className={css.timeDisplay}>
                    {fmtTime(currentTime)} / {fmtTime(project.totalDuration)}
                  </span>
                </div>
              </div>
            ) : (
              <div className={css.emptyState}>
                <Film size={48} />
                <h3>{config.label} Video Editor</h3>
                <p>Add scenes, generate images or video clips, and build your {config.label.toLowerCase()} video.</p>
              </div>
            )}
          </div>

          {/* ── Timeline ── */}
          <div className={css.timeline}>
            <div className={css.timelineHeader}>
              <span>Timeline — {config.label}</span>
              <span>{fmtTime(currentTime)} / {fmtTime(project.totalDuration)}</span>
            </div>
            <div className={css.timelineTracks}>
              {/* Scenes track */}
              <div className={css.timelineTrack}>
                <span className={css.trackLabel}>Scenes</span>
                <div className={css.trackContent} style={{ minWidth: timelineWidth }}>
                  {project.scenes.map((scene, i) => (
                    <div
                      key={scene.id}
                      className={css.sceneBlock}
                      style={{
                        left: scene.startTime * PX_PER_SEC,
                        width: Math.max(20, scene.duration * PX_PER_SEC),
                        background: SCENE_COLORS[i % SCENE_COLORS.length],
                      }}
                      onClick={() => setActiveSceneIdx(i)}
                    >
                      {scene.imageUrl && <img className={css.sceneBlockImg} src={scene.imageUrl} alt="" />}
                      <span className={css.sceneBlockLabel}>S{i + 1}</span>
                    </div>
                  ))}
                  <div className={css.playhead} style={{ left: currentTime * PX_PER_SEC }}>
                    <div className={css.playheadKnob} style={{ background: config.color }} />
                  </div>
                </div>
              </div>

              {/* Text overlays track */}
              <div className={css.timelineTrack}>
                <span className={css.trackLabel}>Text</span>
                <div className={css.trackContent} style={{ minWidth: timelineWidth }}>
                  {project.textOverlays.map((overlay) => (
                    <div
                      key={overlay.id}
                      className={css.lyricBlock}
                      style={{
                        left: overlay.time * PX_PER_SEC,
                        width: Math.max(20, overlay.duration * PX_PER_SEC),
                        borderColor: config.color + '66',
                        background: config.color + '22',
                        color: config.color,
                      }}
                      title={overlay.text}
                    >
                      {overlay.text || `[${overlay.style}]`}
                    </div>
                  ))}
                  <div className={css.playhead} style={{ left: currentTime * PX_PER_SEC }}>
                    <div className={css.playheadKnob} style={{ background: config.color }} />
                  </div>
                </div>
              </div>

              {/* Audio track */}
              <div className={css.timelineTrack}>
                <span className={css.trackLabel}>Audio</span>
                <div className={css.trackContent} style={{ minWidth: timelineWidth }}>
                  {project.audioUrl && (
                    <div
                      className={css.sceneBlock}
                      style={{ left: 0, width: project.audioDuration * PX_PER_SEC, background: config.gradient }}
                    >
                      <IoIosMusicalNotes size={14} style={{ position: 'relative', zIndex: 1 }} />
                      <span className={css.sceneBlockLabel} style={{ marginLeft: 4 }}>Audio</span>
                    </div>
                  )}
                  <div className={css.playhead} style={{ left: currentTime * PX_PER_SEC }}>
                    <div className={css.playheadKnob} style={{ background: config.color }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {statusMsg && (
        <div style={{
          position: 'fixed', bottom: 16, right: 16, background: 'var(--card)',
          border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px',
          fontSize: '0.8rem', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}>
          {statusMsg}
        </div>
      )}
    </div>
  );
}
